'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { ImagePlus, Trash2, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type StoredImage = {
  id: string
  dataUrl: string
  insertedAt: number | null
  createdAt: number
}

type ImageSelectorDialogProps = {
  open: boolean
  onClose: () => void
  onInsert: (image: string, targetIndex: number | null) => void
  onInsertSequence: (images: string[]) => void
  targetIndex: number | null
  portalTarget?: HTMLElement | null
}

const STORAGE_KEY = 'snapbracket:image-library'
const EXPIRY_DAYS = 3
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000

const builtinImages = Array.from({ length: 24 }, (_, index) => `/team/${index + 1}.webp`)

function getStorage(): StoredImage[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as StoredImage[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function persistStorage(images: StoredImage[]) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(images))
  } catch {
    // ignore quota errors
  }
}

function removeExpired(images: StoredImage[]) {
  const now = Date.now()
  const cutoff = now - EXPIRY_MS
  return images.filter((item) => !item.insertedAt || item.insertedAt >= cutoff)
}

function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `img-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

async function compressImage(file: File, size = 220) {
  const image = await loadImage(file)
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas unavailable')

  const minSide = Math.min(image.width, image.height)
  const sx = (image.width - minSide) / 2
  const sy = (image.height - minSide) / 2

  ctx.clearRect(0, 0, size, size)
  ctx.drawImage(image, sx, sy, minSide, minSide, 0, 0, size, size)

  return canvas.toDataURL('image/webp', 0.92)
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = (error) => {
      URL.revokeObjectURL(url)
      reject(error)
    }
    image.src = url
  })
}

export function ImageSelectorDialog({
  open,
  onClose,
  onInsert,
  onInsertSequence,
  targetIndex,
  portalTarget,
}: ImageSelectorDialogProps) {
  const t = useTranslations()
  const [uploaded, setUploaded] = React.useState<StoredImage[]>([])
  const [mounted, setMounted] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const singleMode = targetIndex !== null

  React.useEffect(() => setMounted(true), [])

  React.useEffect(() => {
    const stored = getStorage()
    const initial = removeExpired(stored)
    setUploaded(initial)
    if (initial.length !== stored.length) {
      persistStorage(initial)
    }
  }, [])

  React.useEffect(() => {
    if (!open) return
    setUploaded((prev) => {
      const cleaned = removeExpired(prev)
      if (cleaned.length !== prev.length) {
        persistStorage(cleaned)
      }
      return cleaned
    })
  }, [open])

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length) return
    const list = Array.from(files).filter((file) => file.type.startsWith('image/'))
    if (!list.length) return
    const now = Date.now()
    const results: StoredImage[] = []

    for (const file of list) {
      try {
        const dataUrl = await compressImage(file)
        results.push({
          id: createId(),
          dataUrl,
          createdAt: now,
          insertedAt: singleMode ? now : null,
        })
      } catch (error) {
        console.error(error)
      }
      if (singleMode) break
    }

    if (!results.length) return

    setUploaded((prev) => {
      const merged = removeExpired([...results, ...prev])
      persistStorage(merged)
      return merged
    })

    if (singleMode) {
      onInsert(results[0].dataUrl, targetIndex)
      onClose()
    }
  }

  const handleInsertAllUploaded = () => {
    if (!uploaded.length || singleMode) return
    const now = Date.now()
    setUploaded((prev) => {
      const updated = prev.map((item) => ({ ...item, insertedAt: now }))
      persistStorage(updated)
      return updated
    })
    onInsertSequence(uploaded.map((item) => item.dataUrl))
    onClose()
  }

  const handleInsertOne = (image: string, id?: string) => {
    if (id) {
      setUploaded((prev) => {
        const next = prev.map((item) =>
          item.id === id ? { ...item, insertedAt: Date.now() } : item,
        )
        persistStorage(next)
        return next
      })
    }
    onInsert(image, targetIndex)
    onClose()
  }

  const handleRemoveOne = (id: string) => {
    setUploaded((prev) => {
      const next = prev.filter((item) => item.id !== id)
      persistStorage(next)
      return next
    })
  }

  const handleClearUploaded = () => {
    setUploaded([])
    persistStorage([])
  }

  const overlay = (
    <div
      className={cn(
        'fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity',
        open ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      aria-hidden={!open}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl bg-background p-4 text-sm text-foreground shadow-2xl sm:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1">
            <h2 className="text-base font-semibold">{t('imageSelector.title')}</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <section>
            <header className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <h3 className="text-base">{t('imageSelector.builtinTitle')}</h3>
              <span className="text-[11px]">{t('imageSelector.builtinAction')}</span>
            </header>
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 md:grid-cols-12">
              {builtinImages.map((src) => (
                <button
                  key={src}
                  type="button"
                  className="group/avatar relative flex aspect-square items-center justify-center bg-muted/40 transition hover:bg-muted/70"
                  onClick={() => handleInsertOne(src)}
                >
                  <img
                    src={src}
                    alt="team"
                    className="h-full w-full object-cover"
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-center bg-black/0 text-[12px] text-white opacity-0 transition group-hover/avatar:bg-black/45 group-hover/avatar:opacity-100 after:content-[attr(data-label)]"
                    data-label={t('imageSelector.insert')}
                  />
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              {t('imageSelector.builtinDescription', { days: EXPIRY_DAYS })}
              {singleMode ? (
                <span className="ml-1">{t('imageSelector.singleModeNote')}</span>
              ) : null}
            </p>
          </section>

          <section>
            <header className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <h3 className="text-base">
                {t('imageSelector.uploadsTitle')}
              </h3>
              <span className="text-[11px]">
                {uploaded.length
                  ? t('imageSelector.uploadedCount', { count: uploaded.length })
                  : t('imageSelector.empty')}
              </span>
            </header>
            <div className="flex items-start gap-3">
              <label className="group relative flex aspect-square w-[80px] shrink-0 cursor-pointer items-center justify-center rounded-md border border-dashed border-muted-foreground/50 bg-muted/40 text-muted-foreground transition hover:border-muted-foreground hover:bg-muted/70 sm:w-[132px] md:w-[110px]">
                <div className="pointer-events-none flex flex-col items-center justify-center text-xs font-medium">
                  <ImagePlus className="mb-1 h-6 w-6" />
                  {t('imageSelector.upload')}
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  multiple={!singleMode}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  onChange={(event) => {
                    handleUpload(event.target.files)
                    event.target.value = ''
                  }}
                />
              </label>
              <div className="flex-1 overflow-x-auto">
                <div className="grid min-h-[120px] grid-rows-2 grid-flow-col auto-cols-[80px] gap-3 pr-1 sm:auto-cols-[132px] md:auto-cols-[110px]">
                  {uploaded.map((item) => (
                    <div
                      key={item.id}
                      className="relative flex aspect-square flex-col overflow-hidden rounded-md bg-muted/30"
                    >
                      <img
                        src={item.dataUrl}
                        alt="uploaded"
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/55 px-2 py-1 text-[11px] text-white">
                        <button
                          type="button"
                          onClick={() => handleInsertOne(item.dataUrl, item.id)}
                          className="transition hover:text-amber-200"
                        >
                          {t('imageSelector.insert')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveOne(item.id)}
                          className="transition hover:text-destructive"
                          title={t('imageSelector.delete')}
                        >
                          {t('imageSelector.delete')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {!uploaded.length && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('imageSelector.emptyHint')}
              </p>
            )}
          </section>

          <div className="mt-5 flex w-full justify-center items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3"
              disabled={!uploaded.length || singleMode}
              onClick={handleInsertAllUploaded}
            >
              <ImagePlus className="mr-2 h-4 w-4" />
              {t('imageSelector.insertAll')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3"
              disabled={!uploaded.length}
              onClick={handleClearUploaded}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('imageSelector.clearUploads')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  if (!mounted) return null

  const target = portalTarget ?? document.body
  return target ? createPortal(overlay, target) : overlay
}
